import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import {
  IChainMenu,
  IChainMenuGroup,
  IChainMenuGroupTableData,
  MenuFile,
} from '../../../interfaces/menu';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { state, style, trigger } from '@angular/animations';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { IRestaurantInfo } from '../../../interfaces/restaurant';
import { EditChainMenuComponent } from '../../modals/edit-chain-menu/edit-chain-menu.component';
import { ElementTablesModalComponent } from '../../modals/element-tables-list/element-tables-modal.component';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { MenuStoreService } from '../../../services/menu/menu-store.service';
import { isEqual } from 'lodash';
import { AddChainMenuComponent } from '../../modals/add-chain-menu/add-chain-menu.component';

@UntilDestroy()
@Component({
  selector: 'app-chain-menu-table',
  templateUrl: './chain-menu-table.component.html',
  styleUrls: ['./chain-menu-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
    ]),
  ],
})
export class ChainMenuTableComponent implements OnInit, AfterViewInit {
  private originalMenus: IChainMenuGroup[] = [];
  public isLoading = false;
  public icons = {
    info: faInfoCircle
  };
  public searchTerm$ = new BehaviorSubject<string>('');
  public menusData: IChainMenuGroupTableData[] = [];
  public edit = false;
  public displayedColumns = ['name', 'language', 'file', 'icon', 'hidden', 'restaurants', 'edit'];
  public expandedElements: number[] = [];
  public dataSource = new MatTableDataSource<IChainMenuGroupTableData>();
  public allRestaurants: IRestaurantInfo[] = [];
  public restaurantNames: { [id: number]: string } = {};
  public pageSizeOptions = [10, 25, 50, 100];
  public pageSize = 25;

  @Input() isEdit = false;
  @Input() chainId!: number;
  @Input() isMainMenu = true;
  @Input() set restaurants(restaurantsInfo: IRestaurantInfo[]) {
    this.allRestaurants = restaurantsInfo;
    this.restaurantNames = restaurantsInfo.reduce(
      (acc: { [id: number]: string }, r) => {
        acc[r.id] = r.name;
        return acc;
      },
      {},
    );
    this.applyFilter(this.searchTerm$.getValue());
  }

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  constructor(
    private activateRoute: ActivatedRoute,
    private menuStore: MenuStoreService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.menuStore.isLoading$.pipe(
      untilDestroyed(this)
    ).subscribe((load) => {
      this.isLoading = load;
    });

    this.menuStore.getChainMenus$.pipe(
      untilDestroyed(this)
    ).subscribe((menus) => {
      this.originalMenus = menus;
      this.menusData = this.buildTableData(menus);
      this.updateDataSource();
    });

    const itemPerPage = sessionStorage.getItem('menu.pageSize');
    this.pageSize = itemPerPage ? parseInt(itemPerPage, 10) : 25;

    this.dataSource.filterPredicate = (data: IChainMenuGroupTableData, searchParam: string) => {
      return this.createFilter(data, searchParam);
    };

    this.activateRoute.queryParams.pipe(
      map(params => params.s ?? ''),
      distinctUntilChanged(),
      untilDestroyed(this)
    ).subscribe((searchParam: string) => {
      this.searchTerm$.next(searchParam);
      this.applyFilter(searchParam);
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  private updateDataSource(): void {
    if (!this.dataSource) {
      this.dataSource = new MatTableDataSource<IChainMenuGroupTableData>();
      this.dataSource.filterPredicate = (data, searchParam) =>
        this.createFilter(data, searchParam);
      this.dataSource.data = [...this.menusData];
    } else {
      const nextData = this.menusData.map((newItem) => {
        const existing = this.dataSource.data.find(d => d.id === newItem.id);
        return existing && isEqual(existing, newItem) ? existing : { ...newItem };
      });

      this.dataSource.data = nextData;
    }
  }

  private buildTableData(menus: IChainMenuGroup[]): IChainMenuGroupTableData[] {
    return menus.map(menu => {
      if (menu.translation && Array.isArray(menu.translation) && menu.translation.length) {
        return {
          ...menu,
          translation: new MatTableDataSource(menu.translation)
        };
      }

      return menu;
    });
  }

  public applyFilter(searchTerm: string): void {
    this.searchTerm$.next(searchTerm);
    this.dataSource.filter = searchTerm;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  public toggleRow(element: IChainMenuGroupTableData): void {
    if (element.translation && (element.translation as MatTableDataSource<IChainMenu>).data?.length) {
      if (this.expandedElements.includes(element.id)) {
        this.expandedElements = this.expandedElements.filter(id => id !== element.id);
      } else {
        this.expandedElements.push(element.id);
      }
    }
  }

  public isExpanded(element: IChainMenuGroupTableData): boolean {
    return this.expandedElements.includes(element.id);
  }

  public openEdit(id: number): void {
    const menuGroup = this.originalMenus.find(menu => menu.id === id);
    this.dialog.open(EditChainMenuComponent, {
      data: {
        chainId: this.chainId,
        isMainMenu: !menuGroup?.happyHours,
        restaurants: this.allRestaurants,
        originalId: menuGroup?.id,
        menusGroup: menuGroup
          ? menuGroup.translation?.length > 0
            ? [menuGroup, ...menuGroup.translation]
            : [menuGroup]
          : []
      }
    });
  }

  public openCopy(id: number): void {
    const menuGroup = this.originalMenus.find(menu => menu.id === id);

    if (!menuGroup) {
      return;
    }

    this.dialog.open(AddChainMenuComponent, {
      data: {
        mode: 'copy',
        chainId: this.chainId,
        happyHours: menuGroup.happyHours,
        allRestaurants: this.allRestaurants,
        source: menuGroup,
      }
    });
  }


  restaurantFilter(restaurant: IRestaurantInfo, query: string): boolean {
    return (
        restaurant.name.toLowerCase().includes(query) ||
        restaurant.email.toLowerCase().includes(query)
    );
  }

  createFilter(data: IChainMenuGroupTableData, searchTerm: string): boolean {
    const querySanitized = searchTerm.trim().toLowerCase();

    const matchesString = (value?: string | null): boolean =>
        value?.toString().toLowerCase().includes(querySanitized) ?? false;

    const matchesFileName = (file: MenuFile | null): boolean =>
        file ? matchesString(file.name) : false;

    const matchesTranslation = (): boolean =>
        data.translation instanceof MatTableDataSource &&
        data.translation.data.some((translation) =>
            [translation.name, translation.language, translation.file?.name]
                .some(matchesString) ||
            (querySanitized === 'hidden' && translation.hidden) ||
            (querySanitized === 'no icon' && !translation.icon)
        );

    const matchesRestaurants = (): boolean => {
      if (data.attachedToAll) {
        return this.allRestaurants.some((r) => this.restaurantFilter(r, querySanitized));
      }

      return data.attachedRestaurantIds?.some((id) => {
        const restaurant = this.allRestaurants.find((r) => r.id === id);
        return restaurant ? this.restaurantFilter(restaurant, querySanitized) : false;
      }) ?? false;
    };

    const matchesNoRestaurants = (): boolean =>
        querySanitized === 'no restaurants' &&
        !data.attachedToAll &&
        (!data.attachedRestaurantIds || data.attachedRestaurantIds.length === 0 || data.attachedRestaurantIds[0] === null);

    return (
        matchesString(data.name) ||
        matchesString(data.language) ||
        matchesFileName(data.file) ||
        (querySanitized === 'hidden' && data.hidden) ||
        (querySanitized === 'no icon' && !data.icon) ||
        matchesNoRestaurants() ||
        matchesRestaurants() ||
        matchesTranslation()
    );
  }

  onPageChange(event: PageEvent): void {
    sessionStorage.setItem('menu.pageSize', event.pageSize.toString());
  }

  showMore(name: string, attachedRestaurantIds: number[]): void {
    this.dialog.open(ElementTablesModalComponent,
      {
        data: {
          type: 'restaurant',
          restaurants: attachedRestaurantIds,
          restaurantsNames: this.restaurantNames,
          name
        }
      }
    );
  }
}
